import { CompanyApiFactory, VendorsApiFactory, VendorsApiVendorRetrieveRequest } from '@/clients/unifiedApi';
import { URLS } from '@/constants';
import { PaginatedResponse } from '@/models/api';
import type {
  BadgeCount,
  Company,
  CompanyPaymentOptionsFetchParams,
  PaymentOption,
  PosOption,
  ShippingPreferences,
} from '@/models/company';
import { HighlightWidget } from '@/models/highlightWidget';
import { PaymentProgramChangeRequest } from '@/models/paymentProgramChangeRequest';
import { Staff } from '@/models/staff';
import { useUserStore } from '@/piniaStores/user/user.store';
import session, { apiSession, apiSlugSession, createSession } from '@/services/session';
import buildPath from '@/utils/buildPath';

import { CompanyDomain } from './models/company';
import { getUnifiedApi } from './unifiedApi';

interface GetCompanyDetailsParams {
  companyId: number;
  isBrandOwner?: boolean;
}
/**
 * Fetches general details of a company.
 * @param {object} [payload] optional to pass params
 * @param {number} [payload.companyId] optional id to fetch a detailed company record
 * @param {boolean} [payload.isBrandOwner] optional to fetch BrandOwner companies
 */
// eslint-disable-next-line require-await
export const getDetails = async ({ companyId, isBrandOwner }: GetCompanyDetailsParams): Promise<Company> => {
  if (isBrandOwner) {
    const res = await apiSlugSession.get<Company>(
      `${URLS.BRAND_OWNER.BASE}/${URLS.BRAND_OWNER.DISTRIBUTORS}/${companyId}/?include_children=licenses`,
    );

    return res.data;
  }

  const response = await session.get<Company>(
    `${URLS.COMPANY.LIST}/${companyId}/?include_children=archived_licenses,licenses`,
  );

  return response.data;
};

export interface SwitchCompanyArgs {
  companyId: Company['id'];
}

/**
 * Switches the active company for the current user.
 */
export function switchCompany({ companyId }: SwitchCompanyArgs) {
  return apiSession.post(URLS.COMPANY.SWITCH, { company_id: companyId });
}

export interface GetCompanyDomainArgs {
  id: number;
}

export async function getCompanyDomain({ id }: GetCompanyDomainArgs) {
  const url = buildPath(URLS.DOMAIN.COMPANY, { id });
  const params = { error_format: 'jsonapi' };

  const response = await session.get<CompanyDomain>(url, { params });

  return response.data;
}

export interface UpdateCompanyDomainArgs {
  id: number;
  data: Partial<CompanyDomain>;
}

export async function updateCompanyDomain({ id, data }: UpdateCompanyDomainArgs) {
  const url = buildPath(URLS.DOMAIN.COMPANY, { id });
  const params = { error_format: 'jsonapi' };

  const response = await session.patch<CompanyDomain>(url, data, { params });

  return response.data;
}

export const updateCompanyImageDomain = async ({ id, image }: { image: File; id: number }) => {
  const session = createSession(URLS.INTERNAL, { shouldTransform: false });
  // Note: This endpoint needs trailing slash at the end since Django is configured that way
  const url = `${URLS.COMPANY.LIST}/${id}/?${URLS.CUSTOM_ERROR}`;

  const formData = new FormData();
  formData.append('image', image);
  const response = await session.patch<Company>(url, formData);

  return response.data;
};

/**
 * Fetches all companies for the current user
 */
export const getCompanies = () => {
  return session
    .get<PaginatedResponse<Company>>(`${URLS.COMPANY.LIST}/?include_children=licenses`)
    .then((response) => response.data);
};

/**
 * Fetches all sellers in the marketplace
 */
export const getSellers = (params: { name: string } = { name: '' }) => {
  return session.get(`${URLS.COMPANY.SELLERS}/`, { params }).then((response) => response.data);
};

export interface GetSellersForBuyerArgs {
  search?: string;
  sellThroughDataShared?: boolean;
}

// gets all sellers that a buyer can do business with
export async function getSellersForBuyer({ search, sellThroughDataShared }: GetSellersForBuyerArgs) {
  const params = {
    search,
    limit: 25,
    sell_through_data_shared: sellThroughDataShared,
  };

  const response = await apiSlugSession.get(URLS.COMPANY.SELLERS, { params });

  return response.data;
}

export interface UpdateBuyerSharedSellThroughDataArgs {
  companies: number[];
}

// updates the seller companies that a buyers can see the sell through
export async function updateBuyerSharedSellThroughData({ companies }: UpdateBuyerSharedSellThroughDataArgs) {
  const response = await apiSlugSession.patch(URLS.COMPANY.SHARED_SELL_THROUGH_DATA, { companies });

  return response.data;
}

/**
 * Updates a single company's information
 * @param {object} data of the company to be updated
 */
export async function updateCompany(companyId: number, params: Partial<Company>) {
  const response = await session.patch<Company>(`${URLS.COMPANY.LIST}/${companyId}/?${URLS.CUSTOM_ERROR}`, params);

  return response.data;
}

/**
 * Fetches recent news, which may be specific to the company (ie. Buyer / Seller)
 */
export const getHighlights = () => {
  return session
    .get<PaginatedResponse<HighlightWidget>>(`${URLS.HIGHLIGHT_WIDGETS}/?${URLS.CUSTOM_ERROR}`)
    .then((response) => response.data);
};

/**
 * Fetches all badge counts that are specicific to the comapny
 * including orders received, draft orders, customer requests, etc
 */
export async function getBadgeCounts(): Promise<BadgeCount | undefined> {
  const response = await session.get(`${URLS.COMPANY.ORDERS_STATS}/`);

  return response.data;
}

/**
 * Get company shipping preferences from API
 */
export const getCompanyShippingPreferences = async () => {
  const response = await apiSlugSession.get<ShippingPreferences>(
    `${URLS.LOGISTICS.BASE}/${URLS.COMPANY.COMPANY_SETTINGS}/?${URLS.CUSTOM_ERROR}`,
  );

  return response.data;
};

/**
 * Update company shipping preferences
 * @param {ShippingPreferences} payload Any parameters for the endpoint.
 * @return {Promise<PaginatedResponse>}
 */
export const updateCompanyShippingPreferences = async (payload: Partial<ShippingPreferences>) => {
  const response = await apiSlugSession.post<ShippingPreferences>(
    `${URLS.LOGISTICS.BASE}/${URLS.COMPANY.COMPANY_SETTINGS}/?${URLS.CUSTOM_ERROR}`,
    payload,
  );

  return response.data;
};

/**
 * Get company shipping preferences from API
 * @return {Promise<PaginatedResponse>}
 */
export const getCompanyStaff = async () => {
  const response = await session.get<PaginatedResponse<Staff>>(`${URLS.COMPANY.STAFF}/active/?${URLS.CUSTOM_ERROR}`);

  return response.data;
};

/**
 * Get payment options for the active company by default.
 * Get payment options for a non-active company with a company `id`.
 * @param data
 * @param [data.id] - optional non-active company id
 * @param [data.active] - optional querystring to filter out LLP Options
 */
export const getCompanyPaymentOptions = async (data: CompanyPaymentOptionsFetchParams = {}) => {
  const { id, ...params } = data;
  const { SETTINGS, CUSTOM_ERROR } = URLS;
  let url = `${SETTINGS.COMPANY_PAYMENT_OPTIONS}/`;
  const errorParams = `?${CUSTOM_ERROR}`;

  if (id) {
    url = `${SETTINGS.SELLER_COMPANY_PAYMENT_OPTIONS}/${id}/`;
  }

  const response = await session.get<PaymentOption[]>(`${url}${errorParams}`, { params });

  return response.data;
};

export const getCompanyPaymentProgramChangeRequests = async () => {
  const response = await apiSlugSession.get<PaymentProgramChangeRequest[]>(
    `${URLS.COMPANY.PAYMENT_PROGRAM_CHANGE_REQUESTS}`,
  );

  return response.data;
};

export const updateCompanyPaymentProgramChangeRequest = async (payload: string[]) => {
  const response = await apiSlugSession.patch<PaymentProgramChangeRequest[]>(
    `${URLS.COMPANY.PAYMENT_PROGRAM_CHANGE_REQUESTS}`,
    {
      public_ids: payload,
    },
  );

  return response.data;
};

/**
 * Save payment options for active companies
 * @return Promise
 */
export const saveCompanyPaymentOptions = async (payload) => {
  const response = await session.post<PaymentOption[]>(
    `${URLS.SETTINGS.COMPANY_PAYMENT_OPTIONS}/?${URLS.CUSTOM_ERROR}`,
    payload,
    {
      params: {
        bulk: true,
      },
    },
  );

  return response.data;
};

/**
 * Broadcasts event to payments to kick off KYB for a specified company
 * @return Promise
 */
export const initiateBankLinkingForCompany = async () => {
  const userStore = useUserStore();

  const response = await session.post(
    `${URLS.COMPANY.LIST}/${userStore.userActiveCompany?.id}/${URLS.COMPANY.INITIATE_BANK_LINK}/`,
  );

  return response;
};

/**
 * Validates and updates metrc key
 */
export async function updateMetrcKey(payload: { companyId: number; metrcKeyData: unknown }) {
  const { companyId, metrcKeyData } = payload;
  const response = await session.patch<Company>(
    `${URLS.COMPANY.LIST}/${companyId}/?${URLS.CUSTOM_ERROR}`,
    metrcKeyData,
  );

  return response.data;
}

export async function getCashbackStats(companySlug?: Company['slug']) {
  if (!companySlug) return;

  const api = await getUnifiedApi(CompanyApiFactory);

  const response = await api.companiesStatsCashbackRetrieve({
    marketplaceSlug: companySlug,
  });

  return response.data;
}

export async function sendCategoryManagementInterest(pos: PosOption) {
  return await apiSlugSession.post(`${URLS.COMPANY.CATEGORY_MANAGEMENT_WAITLIST}`, { pos });
}

export const getVendorCompanyDetails = async (data: VendorsApiVendorRetrieveRequest) => {
  const api = await getUnifiedApi(VendorsApiFactory);

  const response = await api.vendorRetrieve(data);

  return response.data;
};
